import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Form, FormField, FormItem, FormControl, FormMessage } from 'components/ui/form';
import UIPhoneInput from 'components/core/phone-input/phone-input';
import { Value } from 'react-phone-number-input';
import { ArrowLeft, HelpCircle } from 'lucide-react';
import { cn } from 'lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import groupLight from '../../assets/images/bebrave-group.svg';
import groupDark from '../../assets/images/bebrave-group-dark.svg';
import { useTheme } from 'components/core/theme-provider';
import { useGenerateCheckoutLink, useBillingAddress, useUpdateSubscription, useCouponInfo, usePlanUpdatePrice } from 'features/subscription/hooks/use-subscription';
import { useSubscriptionStore } from 'state/store/subscription/useSubscriptionStore';
import { PLANS, PURCHASE_CHANNEL } from 'constant/plan';
import { ORGANIZATION_CONSTANTS } from 'constant/organization';
import { useConfig } from 'hooks/use-config';
import { useLanguageContext } from 'i18n/language-context';
import { validatePhoneNumber } from 'utils/validation/phone-validation';
import { GenerateCheckoutLinkRequest, GetCouponInfoResponse } from 'features/subscription';
import { COUNTRIES } from 'constant/countries';
import { processFeatureDisplay } from 'pages/services/utilityService';

interface SubscriptionItem {
    itemId: string;
    price: {
        itemId: string;
        unitPrice: number;
        discountPercent: number;
        taxPercent: number;
        paymentInterval: number;
        paymentIntervalUnit: number;
        actualPrice: number;
        discountedPrice: number;
        discountAmount: number;
        taxAmount: number;
    };
    planPrice: number;
    subTotalPrice: number;
    totalTax: number;
    totalPrice: number;
    title: string;
    description: string;
    baseItem: boolean;
    active: boolean;
    externalId: string | null;
}

interface CheckoutState {
    planId: string;
    planName: string;
    price: number | string;
    period?: string;
    currency: string;
    // Pre-calculated items for easy access
    monthlyItem?: SubscriptionItem;
    annualItem?: SubscriptionItem;
    planItems?: SubscriptionItem[];
    discountPercentage?: number;
    // Original source that brought user to subscription before checkout
    originalSource?: string;
    // Complete subscription plan data
    subscriptionPlan?: {
        itemId: string;
        items: SubscriptionItem[];
        planPrice: number;
        name: string;
        title: string;
        description: string;
        order: number;
        externalId: string | null;
        active: boolean;
        free: boolean;
        business: boolean;
        defaultPlan: boolean;
        trialPeriodDays: number;
        features: Array<{
            key: string;
            active: boolean;
            translationKey: string;
            value: string;
        }>;
        currency: string;
    };
}

// Validation schema for billing address form - translations will be handled in component
const createBillingAddressSchema = (t: (key: string) => string, requireOrganization = false) => z.object({
    email: z.string().min(1, t('EMAIL_REQUIRED')).email(t('VALID_EMAIL_REQUIRED')),
    name: z.string().min(1, t('NAME_REQUIRED')),
    phone: z.string()
        .refine(
            (val) => !val || /^\+[1-9]\d{1,14}$/.test(val),
            { message: t('INVALID_PHONE_NUMBER') }
        )
        .refine(
            validatePhoneNumber,
            { message: t('PHONE_NUMBER_TOO_SHORT') }
        )
        .optional(),
    address: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postcode: z.string().optional(),
    country: z.string().min(1, t('COUNTRY_REQUIRED')),
    saveInformation: z.boolean().default(false),
    // Organization fields - required when upgrading to BUSINESS
    organizationName: requireOrganization
        ? z.string().min(1, t('ORGANIZATION_NAME_REQUIRED') || 'Organization name is required')
        : z.string().optional(),
    organizationEmail: requireOrganization
        ? z.string().min(1, t('ORGANIZATION_EMAIL_REQUIRED') || 'Organization email is required').email(t('VALID_EMAIL_REQUIRED'))
        : z.string().optional(),
});

type BillingFormData = z.infer<ReturnType<typeof createBillingAddressSchema>>;

export function CheckoutPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const checkoutData = location.state as CheckoutState;
    const { theme } = useTheme();

    // Determine if dark mode is active
    const [isDarkMode, setIsDarkMode] = React.useState(() => {
        if (theme === 'dark') return true;
        if (theme === 'light') return false;
        // theme === 'system'
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
    });

    // Update dark mode when theme changes or system preference changes
    React.useEffect(() => {
        if (theme === 'dark') {
            setIsDarkMode(true);
            return;
        }
        if (theme === 'light') {
            setIsDarkMode(false);
            return;
        }

        // theme === 'system'
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        setIsDarkMode(mediaQuery.matches);

        const handleChange = (e: MediaQueryListEvent) => {
            setIsDarkMode(e.matches);
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [theme]);

    const [selectedItemIndex, setSelectedItemIndex] = useState<number>(0);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; couponResponse: GetCouponInfoResponse } | null>(null);
    const [couponError, setCouponError] = useState<string | null>(null);
    const { configurationSettings } = useConfig();
    const { currentLanguage } = useLanguageContext();


    // Fetch billing address for prefilling
    const { data: billingAddressResponse } = useBillingAddress();

    // Get current plan from store to check if billing form should be shown
    const { currentPlan } = useSubscriptionStore();

    // Determine if billing form should be shown
    // Show billing form if current plan is null OR current plan is free
    /**
     * Determines when to show the billing form during checkout.
     * 
     * Show billing form when:
     * - No current plan exists (!currentPlan)
     * - User has a non-free plan that has expired (!(currentPlan.name == PLANS.FREE) && currentPlan?.endDate != null)
     * - User has a free plan (currentPlan.name == PLANS.FREE)
     * - User has no plan assigned (currentPlan.name == PLANS.NOPLAN)
     * - User's current subscription was purchased outside of web (mobile app) (currentPlan.purchaseChannel != PURCHASE_CHANNEL.WEB)
     * 
     * This ensures billing information is collected for new subscriptions, upgrades from free/expired plans,
     * and when switching from mobile app subscriptions to web subscriptions.
     */

    const isFreeOrNoPlan = currentPlan && [PLANS.FREE, PLANS.NOPLAN].includes(currentPlan.name as any);
    const isNonFreeWithEndDate = currentPlan && currentPlan.name !== PLANS.FREE && currentPlan.endDate != null;
    const isNotWebPurchase = currentPlan?.purchaseChannel !== PURCHASE_CHANNEL.WEB;

    const shouldShowBillingForm = !currentPlan || isFreeOrNoPlan || isNonFreeWithEndDate || isNotWebPurchase;
    //const shouldShowBillingForm = !currentPlan || (!(currentPlan.name == PLANS.FREE) && currentPlan?.endDate != null) || currentPlan.name == PLANS.FREE || currentPlan.name == PLANS.NOPLAN || currentPlan.purchaseChannel != PURCHASE_CHANNEL.WEB;

    // Default to Pro plan if no data is provided
    const defaultPlan = {
        planId: 'pro',
        planName: 'EagleGPT Pro',
        price: 15,
        period: 'month',
        currency: 'USD'
    };

    const selectedPlan = checkoutData || defaultPlan;

    // Determine if organization fields should be shown in Plan Details (left side)
    // Show when user is upgrading from BASIC/FREE/NOPLAN to BUSINESS and not showing billing form
    // Hide if user already has a non-default organization set up
    const hasNonDefaultOrganization = billingAddressResponse?.data?.organization?.name &&
        billingAddressResponse.data.organization.name !== ORGANIZATION_CONSTANTS.DEFAULT_ORGANIZATION_NAME;

    const shouldShowOrgFieldsInPlanDetails = !shouldShowBillingForm &&
        selectedPlan.subscriptionPlan?.name === PLANS.BUSINESS &&
        currentPlan &&
        (currentPlan.name === PLANS.FREE || currentPlan.name === PLANS.NOPLAN || currentPlan.name === PLANS.BASIC) &&
        !hasNonDefaultOrganization;

    // Form setup with validation - organization fields required when upgrading to BUSINESS AND user doesn't have existing organization
    const requireOrganization = shouldShowOrgFieldsInPlanDetails || (shouldShowBillingForm && selectedPlan.subscriptionPlan?.name === PLANS.BUSINESS && !hasNonDefaultOrganization);
    const billingAddressSchema = createBillingAddressSchema(t, requireOrganization);
    const form = useForm<BillingFormData>({
        resolver: zodResolver(billingAddressSchema),
        defaultValues: {
            email: '',
            name: '',
            phone: '',
            address: '',
            city: '',
            state: '',
            postcode: '',
            country: '',
            saveInformation: false,
            organizationName: '',
            organizationEmail: '',
        },
    });

    const { handleSubmit, control, setValue, getValues, trigger, register, formState } = form;

    const PAYMENT_INTERVAL_UNITS = [
        { value: 0, label: "Daily", interval: 1, intervalUnit: 0 },
        { value: 1, label: "Weekly", interval: 1, intervalUnit: 1 },
        { value: 2, label: "Monthly", interval: 1, intervalUnit: 2 },
        { value: 3, label: "Bi-Annually", interval: 6, intervalUnit: 2 },
        { value: 4, label: "Yearly", interval: 1, intervalUnit: 3 },
    ] as const;

    // Prefill form with billing address data when available
    useEffect(() => {
        if (billingAddressResponse?.data && !billingAddressResponse?.errors) {
            const billingData = billingAddressResponse.data;
            setValue('email', billingData.email || '', { shouldValidate: true });
            setValue('name', billingData.name || '', { shouldValidate: true });
            setValue('phone', billingData.phone || '', { shouldValidate: true });
            setValue('address', billingData.address || '', { shouldValidate: true });
            setValue('city', billingData.city || '', { shouldValidate: true });
            setValue('state', billingData.state || '', { shouldValidate: true });
            setValue('postcode', billingData.postcode || '', { shouldValidate: true });
            setValue('country', billingData.country || '', { shouldValidate: true });
        }
    }, [billingAddressResponse, setValue]);

    // Use pre-calculated data from subscription page or extract from currentPlan.items
    const planItems = selectedPlan.planItems;

    // Map planItems to include display labels based on payment interval
    let mappedPlanItems = planItems?.map(item => {
        const matchingUnit = PAYMENT_INTERVAL_UNITS.find(
            unit => unit.interval === item.price.paymentInterval &&
                unit.intervalUnit === item.price.paymentIntervalUnit
        );
        return {
            ...item,
            displayLabel: matchingUnit?.label || 'Monthly'
        };
    });

    const hasItems = currentPlan?.items && currentPlan?.items?.length > 0;
    const isPaidPlan = currentPlan && ![PLANS.FREE, PLANS.NOPLAN].includes(currentPlan.name as any);
    const isWebPurchase = currentPlan?.purchaseChannel === PURCHASE_CHANNEL.WEB;

    const showItemBasedOnPreviousPlan = isPaidPlan && hasItems && isWebPurchase;

    // If showItemBasedOnPreviousPlan is true, filter mappedPlanItems to only include items that match currentPlan payment intervals
    if (showItemBasedOnPreviousPlan && currentPlan?.items) {
        const currentPlanItems = currentPlan.items;

        // Filter mappedPlanItems to only keep items that have matching payment intervals in currentPlanItems
        mappedPlanItems = mappedPlanItems?.filter(mappedItem => {
            return currentPlanItems.some(currentItem =>
                currentItem.price.paymentInterval === mappedItem.price.paymentInterval &&
                currentItem.price.paymentIntervalUnit === mappedItem.price.paymentIntervalUnit
            );
        });
    }

    // Auto-set selected item index based on availability
    useEffect(() => {
        if (mappedPlanItems && mappedPlanItems.length > 0) {
            // Ensure selected index is within bounds
            if (selectedItemIndex >= mappedPlanItems.length) {
                setSelectedItemIndex(0);
            }
        }
    }, [mappedPlanItems, selectedItemIndex]);

    // Current selection values
    const currentItem = mappedPlanItems && mappedPlanItems.length > 0 ? mappedPlanItems[selectedItemIndex] : null;

    // Prepare request for plan update price API - only when showItemBasedOnPreviousPlan is true
    const planUpdatePriceRequest = showItemBasedOnPreviousPlan && selectedPlan.subscriptionPlan && currentItem
        ? {
            PlanId: selectedPlan.subscriptionPlan.itemId,
            PlanItemId: currentItem.itemId
        }
        : null;

    // Fetch plan update price when conditions are met
    const { data: planUpdatePriceData } = usePlanUpdatePrice(planUpdatePriceRequest);

    // Get current price and tax from selected item
    const currentPrice = currentItem ? currentItem.totalPrice : 0;
    const tax = currentItem ? currentItem.totalTax : 0;

    // Use API calculated totals when available, otherwise use calculated values
    // Priority: 1) appliedCoupon, 2) planUpdatePriceData (when showItemBasedOnPreviousPlan), 3) currentItem, 4) fallback values
    const subtotal = appliedCoupon
        ? appliedCoupon.couponResponse.subTotalPrice
        : (planUpdatePriceData?.isSuccess
            ? planUpdatePriceData.subTotalPrice
            : (currentItem ? currentItem.subTotalPrice : currentPrice));

    const finalTax = appliedCoupon
        ? appliedCoupon.couponResponse.totalTax
        : (planUpdatePriceData?.isSuccess
            ? planUpdatePriceData.totalTax
            : (currentItem ? currentItem.totalTax : tax));

    const total = appliedCoupon
        ? appliedCoupon.couponResponse.totalPrice
        : (planUpdatePriceData?.isSuccess
            ? planUpdatePriceData.totalPrice
            : (currentItem ? currentItem.totalPrice : currentPrice + tax));

    // Generate checkout link mutation
    const generateCheckoutLinkMutation = useGenerateCheckoutLink({
        onSuccess: (response) => {
            // Set redirecting state and redirect to the checkout URL
            setIsRedirecting(true);
            // Check if coupon provides 100% discount - redirect to success page directly
            if (appliedCoupon && appliedCoupon.couponResponse.couponDiscountPercent === 100) {
                window.location.href = '/payment-success';
                return;
            }
            window.location.href = response.link;
        },
        onError: () => {
            // Reset redirecting state on error
            setIsRedirecting(false);
        },
    });

    // Update subscription mutation
    const updateSubscriptionMutation = useUpdateSubscription({
        onSuccess: async (response) => {
            setIsRedirecting(true);
            window.location.href = response.link;
        },
        onError: () => {
            // Reset redirecting state on error
            setIsRedirecting(false);
        },
    });

    // Coupon validation mutation
    const couponMutation = useCouponInfo({
        onSuccess: (response) => {
            if (response.isSuccess && response.couponDiscountPercent !== null) {
                setAppliedCoupon({
                    code: couponCode,
                    couponResponse: response
                });
                setCouponError(null);
            }
        },
        onError: (error: any) => {
            setAppliedCoupon(null);
            // Extract error message from the error object
            const errorMessage = error?.message || t('INVALID_COUPON') || 'Invalid coupon code';
            setCouponError(errorMessage);
        },
    });

    const handleApplyCoupon = () => {
        if (!couponCode.trim()) {
            return;
        }

        if (!selectedPlan.subscriptionPlan?.itemId) {
            console.error('No plan selected for coupon validation');
            return;
        }

        // Clear any previous errors when applying
        setCouponError(null);

        couponMutation.mutate({
            promoCode: couponCode.trim(),
            planId: selectedPlan.subscriptionPlan.itemId,
            planItemId: currentItem?.itemId || ''
        });
    };

    const onSubmit = async (formData: BillingFormData) => {
        // Get the current selected plan item
        const currentPlanItem = currentItem;
        if (!currentPlanItem || !selectedPlan.subscriptionPlan) {
            alert(t('PLEASE_SELECT_PLAN'));
            return;
        }

        // Validate the form manually to ensure all required fields are filled
        const isValid = await trigger();
        if (!isValid) {
            return; // Stop submission if validation fails
        }

        // Prepare the request payload
        const checkoutData: GenerateCheckoutLinkRequest = {
            PlanId: selectedPlan.subscriptionPlan.itemId,
            PlanItemId: currentPlanItem.itemId,
            BillingAddress: {
                email: formData.email,
                name: formData.name,
                phone: formData.phone || '',
                address: formData.address || '',
                city: formData.city || '',
                state: formData.state || '',
                postcode: formData.postcode || '',
                country: formData.country,
                vat: null, // Send default null value
            },
            // Only include Organization data if user doesn't have existing organization and fields are provided
            Organization: selectedPlan.subscriptionPlan?.name === PLANS.BUSINESS && !hasNonDefaultOrganization && (formData.organizationName || formData.organizationEmail) ? {
                email: formData.organizationEmail || '',
                name: formData.organizationName || '',
                address: [formData.address, formData.city, formData.state, formData.postcode, formData.country].filter(Boolean).join(', '),
            } : undefined,
            PromoCode: appliedCoupon?.code || null
        };

        generateCheckoutLinkMutation.mutate(checkoutData);
    };

    const handleBackToPlans = () => {
        // Get the 'from' parameter to pass back to subscription page
        const fromRoute = searchParams.get('from');

        // Check if there's tab information in the checkout state
        const currentTab = location.state?.currentTab;

        // Build subscription URL with tab and from parameters
        let subscriptionUrl = '/subscription';
        const params = new URLSearchParams();

        if (currentTab) {
            params.append('tab', currentTab);
        }

        if (fromRoute) {
            params.append('from', fromRoute);
        }

        if (params.toString()) {
            subscriptionUrl += `?${params.toString()}`;
        }

        navigate(subscriptionUrl);
    };

    const handleChangePlan = async () => {
        // Get the current selected plan item
        const currentPlanItem = currentItem;
        if (!currentPlanItem || !selectedPlan.subscriptionPlan) {
            console.error('No plan or plan item selected');
            return;
        }

        // Validate form if organization fields are required
        if (shouldShowOrgFieldsInPlanDetails) {
            const isValid = await trigger(['organizationName', 'organizationEmail', 'address', 'city', 'state', 'postcode', 'country']);
            if (!isValid) {
                return; // Stop execution if validation fails
            }
        }

        // Check if coupon provides 100% discount - redirect to success page directly
        if (appliedCoupon && appliedCoupon.couponResponse.couponDiscountPercent === 100) {
            window.location.href = '/payment-success';
            return;
        }

        // Get current form values for organization data
        const formValues = getValues();

        // Prepare the request payload for updating subscription
        const updateData = {
            PlanId: selectedPlan.subscriptionPlan.itemId,
            PlanItemId: currentPlanItem.itemId,
            // Include organization data if upgrading to BUSINESS plan and fields are filled
            ...(shouldShowOrgFieldsInPlanDetails && (formValues.organizationName || formValues.organizationEmail) && {
                Organization: {
                    email: formValues.organizationEmail || '',
                    name: formValues.organizationName || '',
                    address: [formValues.address, formValues.city, formValues.state, formValues.postcode, formValues.country].filter(Boolean).join(', '),
                }
            })
        };

        updateSubscriptionMutation.mutate(updateData);
    };

    const handlePrivacyPolicy = () => {
        const languageCode = currentLanguage.split('-')[0];
        if (languageCode === 'de') {
            window.open(configurationSettings.paths.subscriptionPrivacyPolicyUrlDE, '_blank');
        } else {
            window.open(configurationSettings.paths.subscriptionPrivacyPolicyUrlEN, '_blank');
        }
    };

    const handleTermsOfService = () => {
        const languageCode = currentLanguage.split('-')[0];
        if (languageCode === 'de') {
            window.open(configurationSettings.paths.subscriptionTermsOfServiceUrlDE, '_blank');
        } else {
            window.open(configurationSettings.paths.subscriptionTermsOfServiceUrlEN, '_blank');
        }
    };

    const showPlanTitle = (
        item: { title: string; description: string } | null | undefined,
        type: "title" | "description"
    ) => {
        if (!item) return "";
        const map: Record<string, { title: string; description: string }> = {
            "Pay Annually": {
                title: t("PAY_ANNUALLY"),
                description: t("PAY_ANNUALLY_DESCRIPTION"),
            },
            "Pay Monthly": {
                title: t("PAY_MONTHLY"),
                description: t("PAY_MONTHLY_DESCRIPTION"),
            },
        };
        return map[item.title]?.[type] ?? (type === "title" ? item.title : item.description);
    };

    const isBusinessPlan = selectedPlan.subscriptionPlan?.name === PLANS.BUSINESS;

    // Custom rounding function: if decimal > 0.50 round to next whole number, if <= 0.50 round to 0.50
    /* const customRound = (value: number): string => {
        const integerPart = Math.floor(value);
        const decimalPart = value - integerPart;

        if (decimalPart > 0.50) {
            return (integerPart + 1).toFixed(2);
        } else if (decimalPart > 0) {
            return (integerPart + 0.50).toFixed(2);
        } else {
            return value.toFixed(2);
        }
    }; */

    const isMonthly = currentItem?.price.paymentIntervalUnit === 2 && currentItem?.price.paymentInterval === 1;

    return (
        <div className={`min-h-screen ${theme === 'dark' ? 'bg-neutral-25' : 'bg-gray-50'} flex flex-col lg:flex-row w-full`}>
            {/* Left Side - Plan Details */}
            <div className={`${shouldShowBillingForm ? 'lg:w-[55%]' : 'w-full'} p-4 sm:p-8 lg:p-20 flex justify-center`}>
                <div className={`w-full ${shouldShowBillingForm ? 'lg:w-[70%] max-w-2xl' : 'lg:w-[40%] max-w-xl'}`}>
                    {/* Header with Logo */}
                    <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
                        <button
                            onClick={handleBackToPlans}
                            className="p-1 hover:bg-accent rounded-full transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center"
                        >
                            <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 text-muted-foreground" />
                        </button>
                        <img
                            src={theme === 'dark' ? groupDark : groupLight}
                            alt="EagleGPT"
                            className="h-8 w-32 sm:h-10 sm:w-40"
                        />
                    </div>

                    <div className="space-y-6 sm:space-y-8 lg:space-y-10">
                        {/* Selected Plan Info */}
                        <div className="space-y-2">
                            <p className="text-muted-foreground text-sm sm:text-base font-medium">{t('SELECTED_PLAN')}:</p>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                                <h1 className="text-xl sm:text-2xl font-semibold text-foreground">{selectedPlan.planName}</h1>
                                <button
                                    onClick={handleBackToPlans}
                                    className="text-primary text-sm sm:text-base font-medium hover:underline self-start sm:self-auto"
                                >
                                    {t('VIEW_ALL_PLANS')}
                                </button>
                            </div>
                        </div>

                        {/* Payment Options */}
                        <div className="space-y-4 sm:space-y-6">
                            {mappedPlanItems && mappedPlanItems.length > 0 ? (
                                mappedPlanItems.map((item, index, array) => {
                                    const isSelected = selectedItemIndex === index;
                                    const hasMultipleOptions = array.length > 1;

                                    return (
                                        <div
                                            key={item.itemId}
                                            className={cn(
                                                "border-2 rounded-xl sm:rounded-2xl p-3 sm:p-4 cursor-pointer transition-all",
                                                isSelected
                                                    ? `border-primary ${isDarkMode ? 'bg-primary-900' : 'bg-primary-100'}`
                                                    : "border-border bg-card hover:border-border/80"
                                            )}
                                            onClick={() => {
                                                setSelectedItemIndex(index);
                                                // Clear applied coupon when switching payment types
                                                if (appliedCoupon) {
                                                    setAppliedCoupon(null);
                                                    setCouponCode('');
                                                    setCouponError(null);
                                                }
                                            }}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-start gap-3">
                                                    {hasMultipleOptions && (
                                                        <div className="flex items-center pt-0.5">
                                                            <div className={cn(
                                                                "w-6 h-6 rounded-full border-2 flex items-center justify-center",
                                                                isSelected ? "border-primary" : "border-muted-foreground/30"
                                                            )}>
                                                                {isSelected && (
                                                                    <div className="w-3 h-3 rounded-full bg-primary" />
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="flex-1">
                                                        <h3 className="text-sm sm:text-base font-medium text-foreground mb-1">
                                                            {showPlanTitle(item, "title")}
                                                        </h3>
                                                        <div className="flex items-end gap-2">
                                                            <span className="text-xl sm:text-2xl font-bold text-foreground">
                                                                {item.totalPrice.toFixed(2)}
                                                            </span>
                                                            <span className="text-xs sm:text-sm text-foreground">
                                                                {selectedPlan.currency}/{item.displayLabel}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                {item.price.discountPercent > 0 && (
                                                    <Badge className="bg-transparent text-success-green font-medium text-xs sm:text-sm">
                                                        {t('SAVE_MONEY', { discount: `${item.price.discountPercent}%` })}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="bg-muted/30 rounded-xl p-6 border border-border/50">
                                    <div className="flex flex-col items-center text-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                                            <svg className="w-6 h-6 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-sm font-medium text-foreground">
                                                {t('NO_MATCHING_PAYMENT_OPTIONS') || 'No Matching Payment Options'}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {t('NO_MATCHING_PAYMENT_OPTIONS_MESSAGE') || 'The selected plan does not have payment options that match your current subscription. Please contact support or select a different plan.'}
                                            </p>
                                        </div>
                                        <Button
                                            onClick={handleBackToPlans}
                                            variant="outline"
                                            className="mt-2"
                                        >
                                            {t('VIEW_ALL_PLANS')}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Plan Features */}
                        {selectedPlan.subscriptionPlan && selectedPlan.subscriptionPlan.features.length > 0 && (
                            <div className="space-y-3 sm:space-y-4">
                                <h3 className="text-base sm:text-lg font-medium text-foreground">{t('WHATS_INCLUDED')}:</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {selectedPlan.subscriptionPlan.features
                                        .filter((feature: { active: boolean }) => feature.active)
                                        .map((feature: { translationKey: string, value: string }, index: number) => {
                                            const { translationKey, displayValue } = processFeatureDisplay(
                                                feature,
                                                selectedPlan.subscriptionPlan?.features || []
                                            );
                                            return (
                                                <div key={index} className="flex items-center gap-2">
                                                    <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                                                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full" />
                                                    </div>
                                                    <span className="text-xs sm:text-sm text-foreground">{t(translationKey, { value: displayValue })}</span>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            </div>
                        )}

                        {/* Billing Summary */}
                        {currentItem && (
                            <div className="space-y-3 sm:space-y-4">
                                <div className="space-y-2">
                                    <div className="flex justify-between items-start sm:items-center gap-2">
                                        <span className="text-sm sm:text-base font-medium text-foreground flex-1">
                                            {showPlanTitle(currentItem, "title")}
                                        </span>
                                        <span className="text-sm sm:text-base font-bold text-foreground">
                                            {currentItem.price.actualPrice} {selectedPlan.currency}
                                        </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {showPlanTitle(currentItem, "description")}
                                    </p>
                                </div>

                                {/* New Plan Required Amount - Show when plan update price is available */}
                                {planUpdatePriceData?.isSuccess && planUpdatePriceData.newPlanRequiredAmount !== undefined && (
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-sm sm:text-base font-medium text-foreground">
                                                {t('NEW_PLAN_AMOUNT') || 'New plan amount'}
                                            </span>
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button type="button" className="inline-flex items-center justify-center">
                                                            <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent className="max-w-xs">
                                                        <p className="text-xs text-white">
                                                            {t('NEW_PLAN_AMOUNT_TOOLTIP') || 'The new plan amount is calculated based on the remaining days in your current plan cycle. This ensures you only pay for the portion of the new plan that covers the time left in your existing cycle. The final amount reflects a prorated charge depending on how many days are left.'}
                                                        </p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                        <span className="text-sm sm:text-base font-bold text-foreground">
                                            {planUpdatePriceData.newPlanRequiredAmount.toFixed(2)} {selectedPlan.currency}
                                        </span>
                                    </div>
                                )}

                                <div className="space-y-3 sm:space-y-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm sm:text-base font-medium text-foreground">
                                            {planUpdatePriceData?.isSuccess
                                                ? `${planUpdatePriceData.totalDiscountPercent}%`
                                                : `${currentItem.price.discountPercent}%`} {t('DISCOUNT')}
                                        </span>
                                        <span className="text-sm sm:text-base font-bold text-foreground">
                                            -{planUpdatePriceData?.isSuccess
                                                ? planUpdatePriceData.totalDiscount.toFixed(2)
                                                : currentItem.price.discountAmount} {selectedPlan.currency}
                                            {/* {customRound(subtotal)} {selectedPlan.currency} */}
                                        </span>
                                    </div>

                                    {/* Current Plan Remaining Amount - Show when plan update price is available */}
                                    {planUpdatePriceData?.isSuccess && planUpdatePriceData.currentPlanRemainingAmount !== undefined && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm sm:text-base font-medium text-foreground">
                                                {t('ADJUSTED_FROM_CURRENT_PACKAGE') || 'Adjusted from current package'}
                                            </span>
                                            <span className="text-sm sm:text-base font-bold text-foreground">
                                                -{planUpdatePriceData.currentPlanRemainingAmount.toFixed(2)} {selectedPlan.currency}
                                            </span>
                                        </div>
                                    )}

                                    {/* Coupon Code Field */}
                                    {!showItemBasedOnPreviousPlan && !isMonthly && <div className="space-y-2">
                                        <div className="relative">
                                            <Input
                                                type="text"
                                                placeholder={t('COUPON_CODE')}
                                                className={`pr-24 ${couponError ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                                                value={couponCode}
                                                onChange={(e) => {
                                                    setCouponCode(e.target.value);
                                                    // Clear error when user starts typing
                                                    if (couponError) {
                                                        setCouponError(null);
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleApplyCoupon();
                                                    }
                                                }}
                                            />
                                            <Button
                                                type="button"
                                                className="absolute right-1 top-1/2 -translate-y-1/2 px-4 py-1 h-8 text-sm"
                                                onClick={handleApplyCoupon}
                                                disabled={couponMutation.isPending || !couponCode.trim()}
                                            >
                                                {couponMutation.isPending ? t('APPLYING') : t('APPLY_COUPON')}
                                            </Button>
                                        </div>

                                        {/* Coupon Error Message */}
                                        {couponError && (
                                            <p className="text-xs text-destructive">
                                                {couponError}
                                            </p>
                                        )}

                                        {/* Coupon Success Message */}
                                        {appliedCoupon && (
                                            <div className="flex items-center justify-between text-xs">
                                                <span className="text-success-green font-medium">
                                                    {/* Coupon &quot;{appliedCoupon.code}&quot; applied ({appliedCoupon.discountPercent}% off) */}
                                                    {t('COUPON_APPLY_MSG', { appliedCouponCode: `"${appliedCoupon.code}"`, appliedCouponDiscountPercent: `${appliedCoupon.couponResponse.couponDiscountPercent}` })}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setAppliedCoupon(null);
                                                        setCouponCode('');
                                                        setCouponError(null);
                                                    }}
                                                    className="text-muted-foreground hover:text-foreground text-xs underline"
                                                >
                                                    {t('REMOVE')}
                                                </button>
                                            </div>
                                        )}
                                    </div>}

                                    {/* Coupon Discount Line */}
                                    {appliedCoupon && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm sm:text-base font-medium text-success-green">
                                                {t('COUPON_DISCOUNT') || `Coupon Discount (${appliedCoupon.couponResponse.couponDiscountPercent}%)`} ({t('COUPON')})
                                            </span>
                                            <span className="text-sm sm:text-base font-bold text-success-green">
                                                -{appliedCoupon.couponResponse.couponDiscount} {selectedPlan.currency}
                                            </span>
                                        </div>
                                    )}

                                    <div className="h-px bg-border" />



                                    <div className="flex justify-between items-center">
                                        <span className="text-sm sm:text-base font-medium text-foreground">{t('SUBTOTAL')}</span>
                                        <span className="text-sm sm:text-base font-bold text-foreground">
                                            {subtotal} {selectedPlan.currency}
                                            {/* {customRound(subtotal)} {selectedPlan.currency} */}
                                        </span>
                                    </div>

                                    <div className="flex justify-between items-center">
                                        <span className="text-xs sm:text-sm font-medium text-foreground">{t('TAX')}</span>
                                        <span className="text-xs sm:text-sm font-medium text-muted-foreground">
                                            {finalTax} {selectedPlan.currency}
                                            {/* {customRound(finalTax)} {selectedPlan.currency} */}
                                        </span>
                                    </div>
                                </div>



                                <div className="h-px bg-border" />



                                <div className="flex justify-between items-center">
                                    <span className="text-sm sm:text-base font-medium text-foreground">{t('TOTAL')} ({t('ROUNDED')})</span>
                                    <span className="text-sm sm:text-base font-bold text-foreground">
                                        {total} {selectedPlan.currency}
                                        {/* {customRound(total)} {selectedPlan.currency} */}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Organization Fields in Plan Details - Show when upgrading to BUSINESS */}
                        {isBusinessPlan && !shouldShowBillingForm && currentPlan && (currentPlan.name === PLANS.FREE || currentPlan.name === PLANS.NOPLAN || currentPlan.name === PLANS.BASIC) && (
                            <>
                                {/* Show organization fields if user doesn't have existing organization */}
                                {shouldShowOrgFieldsInPlanDetails && (
                                    <div className="space-y-4 sm:space-y-6">
                                        <div className="flex items-center space-x-2">
                                            <div className="h-px bg-border flex-1" />
                                            <span className="text-xs text-muted-foreground px-3">{t('ORGANIZATION_INFO') || 'Organization Information'}</span>
                                            <div className="h-px bg-border flex-1" />
                                        </div>

                                        <div className="space-y-4 sm:space-y-5">
                                            {/* Organization Name */}
                                            <div className="space-y-2">
                                                <Label className="text-xs sm:text-sm text-foreground">
                                                    {t('ORGANIZATION_NAME') || 'Organization Name'}*
                                                </Label>
                                                <Input
                                                    type="text"
                                                    placeholder={t('ORGANIZATION_NAME_PLACEHOLDER') || 'Enter organization name'}
                                                    className="h-10 sm:h-9"
                                                    {...register('organizationName')}
                                                />
                                                {formState.errors.organizationName && (
                                                    <p className="text-xs text-destructive">
                                                        {formState.errors.organizationName.message}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Organization Email */}
                                            <div className="space-y-2">
                                                <Label className="text-xs sm:text-sm text-foreground">
                                                    {t('ORGANIZATION_EMAIL') || 'Organization Email'}*
                                                </Label>
                                                <Input
                                                    type="email"
                                                    placeholder={t('ORGANIZATION_EMAIL_PLACEHOLDER') || 'Enter organization email'}
                                                    className="h-10 sm:h-9"
                                                    {...register('organizationEmail')}
                                                />
                                                {formState.errors.organizationEmail && (
                                                    <p className="text-xs text-destructive">
                                                        {formState.errors.organizationEmail.message}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Organization Address */}
                                            <div className="space-y-2">
                                                <Label className="text-xs sm:text-sm text-foreground">
                                                    {t('ORGANIZATION_ADDRESS') || 'Organization Address'}*
                                                </Label>
                                                <Input
                                                    type="text"
                                                    placeholder={t('ORGANIZATION_ADDRESS_PLACEHOLDER') || 'Enter organization address'}
                                                    className="h-10 sm:h-9"
                                                    {...register('address')}
                                                />
                                                {formState.errors.address && (
                                                    <p className="text-xs text-destructive">
                                                        {formState.errors.address.message}
                                                    </p>
                                                )}
                                            </div>

                                            {/* City and State - Two Columns */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label className="text-xs sm:text-sm text-foreground">
                                                        {t('CITY') || 'City'}
                                                    </Label>
                                                    <Input
                                                        type="text"
                                                        placeholder={t('CITY') || 'Enter city'}
                                                        className="h-10 sm:h-9"
                                                        {...register('city')}
                                                    />
                                                    {formState.errors.city && (
                                                        <p className="text-xs text-destructive">
                                                            {formState.errors.city.message}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-xs sm:text-sm text-foreground">
                                                        {t('STATE') || 'State/Province'}
                                                    </Label>
                                                    <Input
                                                        type="text"
                                                        placeholder={t('STATE') || 'Enter state'}
                                                        className="h-10 sm:h-9"
                                                        {...register('state')}
                                                    />
                                                    {formState.errors.state && (
                                                        <p className="text-xs text-destructive">
                                                            {formState.errors.state.message}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Postcode and Country - Two Columns */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label className="text-xs sm:text-sm text-foreground">
                                                        {t('POSTCODE') || 'Postcode'}
                                                    </Label>
                                                    <Input
                                                        type="text"
                                                        placeholder={t('POSTCODE') || 'Enter postcode'}
                                                        className="h-10 sm:h-9"
                                                        {...register('postcode')}
                                                    />
                                                    {formState.errors.postcode && (
                                                        <p className="text-xs text-destructive">
                                                            {formState.errors.postcode.message}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-xs sm:text-sm text-foreground">
                                                        {t('COUNTRY') || 'Country'}
                                                    </Label>
                                                    <Select onValueChange={(value) => setValue('country', value)} value={getValues('country') || undefined}>
                                                        <SelectTrigger className="h-10 sm:h-9">
                                                            <SelectValue placeholder={t('SELECT_COUNTRY') || 'Select country'} />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {COUNTRIES.map((country) => (
                                                                <SelectItem key={country} value={country}>
                                                                    {country}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    {formState.errors.country && (
                                                        <p className="text-xs text-destructive">
                                                            {formState.errors.country.message}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                )}

                                {/* Show message if user already has organization */}
                                {hasNonDefaultOrganization && (
                                    <div className="space-y-4 sm:space-y-6">
                                        <div className="flex items-center space-x-2">
                                            <div className="h-px bg-border flex-1" />
                                            <span className="text-xs text-muted-foreground px-3">{t('ORGANIZATION_INFO') || 'Organization Information'}</span>
                                            <div className="h-px bg-border flex-1" />
                                        </div>

                                        <div className="bg-muted/30 rounded-xl p-4 border border-border/50">
                                            <div className="flex items-start gap-3">
                                                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                                                    <div className="w-2 h-2 bg-white rounded-full" />
                                                </div>
                                                <div className="space-y-1">
                                                    <p className="text-sm font-medium text-foreground">
                                                        {t('ORGANIZATION_ALREADY_SET') || 'Organization Already Set'}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {t('ORGANIZATION_EXISTS_MESSAGE') || 'This user already has an organization configured.'}
                                                        <br />
                                                        <span className="font-medium">{billingAddressResponse?.data?.organization?.name}</span>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {!shouldShowBillingForm && (
                            <Button
                                type="button"
                                className="w-full h-12 sm:h-10 font-semibold text-sm sm:text-base"
                                onClick={handleChangePlan}
                                disabled={updateSubscriptionMutation.isPending || !mappedPlanItems || mappedPlanItems.length === 0}
                            >
                                {updateSubscriptionMutation.isPending ? t('CHANGING_PLAN') : t('CHANGE_PLAN')}
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Right Side - Billing Information */}
            {shouldShowBillingForm && (
                <div className="lg:w-[45%] w-full flex justify-center items-start bg-card lg:rounded-tl-[32px] lg:rounded-bl-[32px] p-4 sm:p-8 lg:p-20">
                    <div className="w-full lg:w-[70%] max-w-lg flex flex-col">
                        <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-6 sm:mb-8">{t('BILLING_INFORMATION')}</h2>

                        <Form {...form}>
                            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8 sm:space-y-12">
                                {/* Form Fields */}
                                <div className="space-y-6 sm:space-y-8">
                                    <div className="space-y-4 sm:space-y-5">
                                        {/* Email - Full Width */}
                                        <FormField
                                            control={control}
                                            name="email"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <Label className="text-xs sm:text-sm text-foreground">{t('EMAIL')}*</Label>
                                                    <FormControl>
                                                        <Input
                                                            type="email"
                                                            placeholder={t('EMAIL')}
                                                            className="bg-background h-12 sm:h-10"
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        {/* Name - Full Width */}
                                        <FormField
                                            control={control}
                                            name="name"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <Label className="text-xs sm:text-sm text-foreground">{t('FULL_NAME')}*</Label>
                                                    <FormControl>
                                                        <Input
                                                            type="text"
                                                            placeholder={t('FULL_NAME')}
                                                            className="h-12 sm:h-10"
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        {/* Phone - Full Width */}
                                        <FormField
                                            control={control}
                                            name="phone"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <Label className="text-xs sm:text-sm text-foreground">{t('PHONE_NUMBER')}</Label>
                                                    <FormControl>
                                                        <UIPhoneInput
                                                            value={field.value as Value}
                                                            onChange={(value: Value) => setValue('phone', value as string)}
                                                            placeholder={t('PHONE_NUMBER_PLACEHOLDER')}
                                                            defaultCountry="CH"
                                                            countryCallingCodeEditable={false}
                                                            international
                                                            className="h-12 sm:h-10"
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>

                                    {/* Organization Section - Show fields for new organizations or message for existing ones */}
                                    {isBusinessPlan && (
                                        <>
                                            {/* Show organization fields if user doesn't have existing organization */}
                                            {!hasNonDefaultOrganization && (
                                                <div className="space-y-4 sm:space-y-5">
                                                    <div className="flex items-center space-x-2">
                                                        <div className="h-px bg-border flex-1" />
                                                        <span className="text-xs text-muted-foreground px-3">{t('ORGANIZATION_INFO') || 'Organization Information'}</span>
                                                        <div className="h-px bg-border flex-1" />
                                                    </div>

                                                    {/* Organization Fields */}
                                                    <div className="space-y-4 sm:space-y-5">
                                                        {/* Organization Name */}
                                                        <FormField
                                                            control={control}
                                                            name="organizationName"
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <Label className="text-xs sm:text-sm text-foreground">
                                                                        {t('ORGANIZATION_NAME') || 'Organization Name'}{requireOrganization && '*'}
                                                                    </Label>
                                                                    <FormControl>
                                                                        <Input
                                                                            type="text"
                                                                            placeholder={t('ORGANIZATION_NAME_PLACEHOLDER') || 'Enter organization name'}
                                                                            className="h-12 sm:h-10"
                                                                            {...field}
                                                                        />
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />

                                                        {/* Organization Email */}
                                                        <FormField
                                                            control={control}
                                                            name="organizationEmail"
                                                            render={({ field }) => (
                                                                <FormItem>
                                                                    <Label className="text-xs sm:text-sm text-foreground">
                                                                        {t('ORGANIZATION_EMAIL') || 'Organization Email'}{requireOrganization && '*'}
                                                                    </Label>
                                                                    <FormControl>
                                                                        <Input
                                                                            type="email"
                                                                            placeholder={t('ORGANIZATION_EMAIL_PLACEHOLDER') || 'Enter organization email'}
                                                                            className="h-12 sm:h-10"
                                                                            {...field}
                                                                        />
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />

                                                    </div>
                                                </div>
                                            )}

                                            {/* Show message if user already has organization */}
                                            {hasNonDefaultOrganization && (
                                                <div className="space-y-4 sm:space-y-5">
                                                    <div className="flex items-center space-x-2">
                                                        <div className="h-px bg-border flex-1" />
                                                        <span className="text-xs text-muted-foreground px-3">{t('ORGANIZATION_INFO') || 'Organization Information'}</span>
                                                        <div className="h-px bg-border flex-1" />
                                                    </div>

                                                    <div className="bg-muted/30 rounded-xl p-4 border border-border/50">
                                                        <div className="flex items-start gap-3">
                                                            <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                                                                <div className="w-2 h-2 bg-white rounded-full" />
                                                            </div>
                                                            <div className="space-y-1">
                                                                <p className="text-sm font-medium text-foreground">
                                                                    {t('ORGANIZATION_ALREADY_SET') || 'Organization Already Set'}
                                                                </p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    {t('ORGANIZATION_EXISTS_MESSAGE') || 'This user already has an organization configured.'}
                                                                    <br />
                                                                    <span className="font-medium">{billingAddressResponse?.data?.organization?.name}</span>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {/* Billing Address Section */}
                                    <div className={`space-y-4 sm:space-y-5 ${isBusinessPlan ? '!mt-4' : ''}`}>
                                        {!isBusinessPlan && <div className="flex items-center space-x-2">
                                            <div className="h-px bg-border flex-1" />
                                            <span className="text-xs text-muted-foreground px-3">{t('BILLING_ADDRESS') || 'Billing Address'}</span>
                                            <div className="h-px bg-border flex-1" />
                                        </div>}

                                        {/* Address - Full Width */}
                                        <FormField
                                            control={control}
                                            name="address"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <Label className="text-xs sm:text-sm text-foreground">{isBusinessPlan ? t('ORGANIZATION_ADDRESS') : t('ADDRESS')}</Label>
                                                    <FormControl>
                                                        <Input
                                                            type="text"
                                                            placeholder={isBusinessPlan ? t('ORGANIZATION_ADDRESS_PLACEHOLDER') : t('ADDRESS')}
                                                            className="h-12 sm:h-10"
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        {/* City and State/Province - Two Columns */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <FormField
                                                control={control}
                                                name="city"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <Label className="text-xs sm:text-sm text-foreground">{t('CITY')}</Label>
                                                        <FormControl>
                                                            <Input
                                                                type="text"
                                                                placeholder={t('CITY')}
                                                                className="h-12 sm:h-10"
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={control}
                                                name="state"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <Label className="text-xs sm:text-sm text-foreground">{t('STATE_PROVINCE')}</Label>
                                                        <FormControl>
                                                            <Input
                                                                type="text"
                                                                placeholder={t('STATE')}
                                                                className="h-12 sm:h-10"
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>

                                        {/* Postcode and Country - Two Columns */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <FormField
                                                control={control}
                                                name="postcode"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <Label className="text-xs sm:text-sm text-foreground">{t('POSTCODE')}</Label>
                                                        <FormControl>
                                                            <Input
                                                                type="text"
                                                                placeholder={t('POSTCODE')}
                                                                className="h-12 sm:h-10"
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={control}
                                                name="country"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <Label className="text-xs sm:text-sm text-foreground">{t('COUNTRY')}*</Label>
                                                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                                                            <FormControl>
                                                                <SelectTrigger className="h-12 sm:h-10">
                                                                    <SelectValue placeholder={t('SELECT_COUNTRY')} />
                                                                </SelectTrigger>
                                                            </FormControl>
                                                            <SelectContent>
                                                                {COUNTRIES.map((country) => (
                                                                    <SelectItem key={country} value={country}>
                                                                        {country}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                    </div>

                                    {/* Terms */}
                                    <div className="space-y-3">
                                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                                            {t('BY_SUBSCRIBING_ACCEPT')}{' '}
                                            <button
                                                type="button"
                                                className="underline hover:no-underline text-primary bg-transparent border-none p-0 font-inherit cursor-pointer"
                                                onClick={handleTermsOfService}
                                            >
                                                {t('TERMS_OF_SERVICE')}
                                            </button>
                                            {' '}{t('AND')}{' '}
                                            <button
                                                type="button"
                                                className="underline hover:no-underline text-primary bg-transparent border-none p-0 font-inherit cursor-pointer"
                                                onClick={handlePrivacyPolicy}
                                            >
                                                {t('PRIVACY_POLICY')}
                                            </button>
                                        </p>
                                    </div>
                                </div>

                                {/* Subscribe Button */}
                                <Button
                                    type="submit"
                                    className="w-full h-12 sm:h-10 font-semibold text-sm sm:text-base"
                                    disabled={generateCheckoutLinkMutation.isPending || isRedirecting || !mappedPlanItems || mappedPlanItems.length === 0}
                                >
                                    {isRedirecting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            <span className="text-sm sm:text-base">{t('REDIRECTING_TO_CHECKOUT')}</span>
                                        </>
                                    ) : generateCheckoutLinkMutation.isPending ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            <span className="text-sm sm:text-base">{t('PROCESSING')}</span>
                                        </>
                                    ) : (
                                        <span className="text-sm sm:text-base">{t('SUBSCRIBE')}</span>
                                    )}
                                </Button>
                            </form>
                        </Form>
                    </div>
                </div>
            )}
        </div>
    );
}